import React from 'react';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import {
    ANALYSIS,
    UNIFIED_ANALYSIS,
    ORDER_DATA_ANALYSIS,
    TAXONOMY_ANALYSIS,
    MANAGE_UNIFIED_SCORING,
    SELECTED,
} from '../constants/CategoryRelevancyConstants';
import { ChevronDown } from 'Components/DesignSystem/Iconography/Icons';
import { DiamondIcon, OrderDataIcon, SlidersIcon, TaxonomyIcon } from 'assests/GmsIcons';

type AnalysisButtonProps = {
    selectedAnalyses: AnalysisOption[];
    onAnalysisChange: (option: AnalysisOption) => void;
    isOpen: boolean;
    onToggle: () => void;
    onManageUnifiedScoring?: () => void;
};

const AnalysisButton = ({
    selectedAnalyses,
    onAnalysisChange,
    isOpen,
    onToggle,
    onManageUnifiedScoring,
}: AnalysisButtonProps) => {
    const isUnified = selectedAnalyses.includes('unified');
    const selectedCount = selectedAnalyses.length;

    /* The data-source glyphs are currentColor now (okf/assests 2026-09-08) —
       ink-secondary keeps the quiet weight the old #6D788C hardcoded. */
    const glyphInk = 'tw-text-ink-secondary tw-inline-flex';

    return (
        <div className="tw-relative">
            <button
                data-testid="category-relevancy-analysis-btn"
                className={`tw-h-8 tw-px-3 tw-flex tw-items-center tw-gap-2 tw-border tw-border-solid tw-rounded tw-text-sm tw-text-ink ${isOpen
                    ? 'tw-bg-surface-hover tw-border-edge-strong'
                    : 'tw-bg-surface tw-border-edge hover:tw-bg-surface-hover'
                    }`}
                onClick={onToggle}
                aria-haspopup="true"
                aria-expanded={isOpen}
            >
                <span className="tw-text-ink-secondary">{ANALYSIS}:</span>
                <span className="tw-font-semibold tw-text-ink">{selectedCount} {SELECTED}</span>
                <span className="tw-text-ink-secondary tw-inline-flex"><ChevronDown size={16} /></span>
            </button>

            {isOpen && (
                <div className="tw-absolute tw-z-10 tw-mt-1 tw-right-0 tw-bg-surface-raised tw-rounded-srf tw-border tw-border-edge tw-shadow-ds-md tw-overflow-hidden tw-min-w-[220px] tw-whitespace-nowrap">

                    {/* Unified Analysis */}
                    <div className="tw-px-3 tw-py-2.5">
                        <div className="tw-flex tw-items-center tw-gap-2.5">
                            <Checkbox
                                checked={isUnified}
                                onChange={() => onAnalysisChange('unified')}
                                size="sm"
                            />
                            <span className={glyphInk}><DiamondIcon /></span>
                            <span className="tw-text-sm tw-text-ink">{UNIFIED_ANALYSIS}</span>
                        </div>
                    </div>

                    <div className="tw-w-full tw-h-px tw-bg-edge" />

                    <div className="tw-px-3 tw-pt-1 tw-pb-0">
                        <span className="tw-text-xs tw-text-ink-secondary">Data source</span>
                    </div>

                    {/* Order Data Analysis */}
                    <div className="tw-px-3 tw-py-2.5 tw-flex tw-items-center tw-gap-2.5">
                        <Checkbox
                            checked={selectedAnalyses.includes('order_data')}
                            onChange={() => onAnalysisChange('order_data')}
                            size="sm"
                        />
                        <span className={glyphInk}><OrderDataIcon /></span>
                        <span className="tw-text-sm tw-text-ink">{ORDER_DATA_ANALYSIS}</span>
                    </div>

                    <div className="tw-w-full tw-h-px tw-bg-edge" />

                    {/* Taxonomy Analysis */}
                    <div className="tw-px-3 tw-py-2.5 tw-flex tw-items-center tw-gap-2.5">
                        <Checkbox
                            checked={selectedAnalyses.includes('taxonomy')}
                            onChange={() => onAnalysisChange('taxonomy')}
                            size="sm"
                        />
                        <span className={glyphInk}><TaxonomyIcon /></span>
                        <span className="tw-text-sm tw-text-ink">{TAXONOMY_ANALYSIS}</span>
                    </div>

                    <div className="tw-w-full tw-h-px tw-bg-edge" />

                    {/* Manage Unified Scoring */}
                    <button data-testid="components-analysisbutton-button-1"
                        className="tw-w-full tw-px-3 tw-py-2.5 tw-flex tw-items-center tw-gap-2.5 hover:tw-bg-surface-hover tw-text-left"
                        onClick={() => { onManageUnifiedScoring?.(); onToggle(); }}
                    >
                        <span className={glyphInk}><SlidersIcon /></span>
                        <span className="tw-text-sm tw-text-ink">{MANAGE_UNIFIED_SCORING}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default AnalysisButton;
